import { Popover, Transition } from "@headlessui/react";
import * as Sentry from "@sentry/nextjs";
import clsx from "clsx";
import firebase from "firebase/app";
import { useTranslation } from "next-i18next";
import React, { Fragment, useMemo, useRef, useState } from "react";
import { usePopper } from "react-popper";
import {
  FacebookIcon,
  FacebookShareButton,
  TelegramIcon,
  TelegramShareButton,
  TwitterIcon,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
} from "react-share";

import { RouteName } from "@/consts";
import { AnalyticsEventEnum } from "@/consts/AnalyticsEventEnum";
import { ShareIcon } from "@/features/common/components/icons/ShareIcon";
import { Text } from "@/features/common/components/tailwind/Text";
import { PostPagePostFragment } from "@/generated/generated-hooks";
import { getPublicConfig } from "@/helpers/getPublicConfig";

interface SocialMediaMenuProps {
  postDetail: Pick<PostPagePostFragment, "_id" | "title" | "slug">;
  onImageCard?: boolean;
}

const analyticsMap = {
  twitter: AnalyticsEventEnum.PostPageShareTwitter,
  facebook: AnalyticsEventEnum.PostPageShareFacebook,
  whatsapp: AnalyticsEventEnum.PostPageShareWhatsapp,
  telegram: AnalyticsEventEnum.PostPageShareTelegram,
};

const shareIconStyle = {
  size: 40,
  round: true,
};

const config = getPublicConfig();

export const PostActionShareMenu: React.FC<SocialMediaMenuProps> = (props) => {
  const { postDetail, onImageCard } = props;
  const { t } = useTranslation("post");

  const { title, slug, _id } = postDetail;
  const shareButtonProps = { url: `${config.domain}${RouteName.Post}/${slug}` };

  const popperElRef = useRef();
  const [referenceElement, setReferenceElement] = useState<any>();
  const [popperElement, setPopperElement] = useState<any>();
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: "bottom-end",
    modifiers: [
      {
        name: "offset",
        options: { offset: [0, 8] },
      },
      { name: "preventOverflow" },
    ],
  });

  const socialMediaOption = [
    {
      content: (
        <WhatsappShareButton {...shareButtonProps}>
          <WhatsappIcon {...shareIconStyle} />
        </WhatsappShareButton>
      ),
      platform: "whatsapp",
    },
    {
      content: (
        <FacebookShareButton {...shareButtonProps}>
          <FacebookIcon {...shareIconStyle} />
        </FacebookShareButton>
      ),
      platform: "facebook",
    },
    {
      content: (
        <TwitterShareButton {...shareButtonProps}>
          <TwitterIcon {...shareIconStyle} />
        </TwitterShareButton>
      ),
      platform: "twitter",
    },
    {
      content: (
        <TelegramShareButton {...shareButtonProps}>
          <TelegramIcon {...shareIconStyle} />
        </TelegramShareButton>
      ),
      platform: "telegram",
    },
  ];

  const onClickPlatformIcon = (method: string) => {
    firebase.analytics().logEvent(analyticsMap[method], {
      postId: _id,
    });
  };

  const onClickShare = async () => {
    firebase.analytics().logEvent(AnalyticsEventEnum.ImageCardClickShare, {
      postId: _id,
    });
    try {
      if (!navigator.share) return;
      await navigator.share({ title, ...shareButtonProps });
      firebase.analytics().logEvent(AnalyticsEventEnum.PostPageShareMobile, {
        postId: _id,
      });
    } catch (e) {
      if (e.message.includes("AbortError")) return;
      Sentry.captureException(e, {
        tags: { component: "PostActionShareMenu", fn: "navigator.share" },
      });
    }
  };

  return (
    <Popover className="relative flex items-center z-40">
      {({ open, close }) => (
        <>
          <Popover.Button
            ref={setReferenceElement}
            className="outline-none ring-0"
          >
            <div
              className="flex items-center touchable space-x-2"
              onClick={() => {
                onClickShare();
              }}
            >
              {onImageCard && <ShareIcon className="text-gray-300 w-5 h-5" />}
              <Text size="normal" className="flex text-gray-500">
                {t("share")}
              </Text>
            </div>
          </Popover.Button>
          <div ref={popperElRef} style={styles.popper} {...attributes.popper}>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
              beforeEnter={() => setPopperElement(popperElRef.current)}
              afterLeave={() => setPopperElement(null)}
            >
              <Popover.Panel
                ref={setPopperElement}
                className={clsx(
                  "z-40 max-w-xs md:max-w-xs bg-white rounded-lg shadow-lg flex",
                  onImageCard ? "flex-row" : "flex-col",
                )}
              >
                {socialMediaOption.map((option, index) => (
                  <div
                    key={index}
                    onClick={() => {
                      onClickPlatformIcon(option.platform);
                      close();
                    }}
                    className="p-2 flex items-center justify-center hover:bg-gray-100 rounded-xl"
                  >
                    {option.content}
                  </div>
                ))}
              </Popover.Panel>
            </Transition>
          </div>
        </>
      )}
    </Popover>
  );
};
